import clsx from 'clsx';
import React from 'react';

import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { ColorPicker } from '@/src/globalComponents';
import { showToast } from '@/src/globalFunction';

import TagPill from './TagPill';

import { TagType } from '../types';
import { isHexColorDark } from '../utils';

interface TagInputCreateProps {
  tagInput: string;
  setTagInput: (val: string) => void;
  suggestions: TagType[];
  onSelectSuggestion: (label: string) => void;
  onCreateTag: (label: string, color: string) => void;
  newTagColor: string;
  setNewTagColor: (color: string) => void;
  showColorPicker: boolean;
  setShowColorPicker: (show: boolean) => void;
  colorPickerRef?: React.RefObject<HTMLDivElement>;
  disableCreateButton?: boolean;
}

const TagInputCreate: React.FC<TagInputCreateProps> = ({
  tagInput,
  setTagInput,
  suggestions,
  onSelectSuggestion,
  onCreateTag,
  newTagColor,
  setNewTagColor,
  showColorPicker,
  setShowColorPicker,
  colorPickerRef,
  disableCreateButton,
}) => {
  return (
    <>
      <input
        type="text"
        placeholder="Search or create new tag"
        value={tagInput}
        onChange={(e) => setTagInput(e.target.value)}
        className={clsx(
          'tw-w-full',
          'tw-border-cfo-grey-600',
          'tw-rounded-md',
          'tw-mx-1',
          'tw-px-2',
          'tw-py-2',
          'tw-text-md',
          'tw-my-4',
          'focus:tw-outline-none',
          'focus:tw-ring-2',
          'focus:tw-ring-blue-500'
        )}
      />
      {tagInput.length > 0 && (
        <>
          <div
            className={clsx(
              'tw-flex',
              'tw-items-start',
              'tw-my-3',
              'tw-relative',
              'tw-min-h-[16px]'
            )}
          >
            <div
              className={clsx('tw-flex', 'tw-flex-wrap', 'tw-gap-2', 'tw-flex-col')}
              style={{
                maxWidth: disableCreateButton ? 200 : 320,
              }}
            >
              {!disableCreateButton && (
                <TagPill
                  key={tagInput + '-preview'}
                  tag={{
                    id: tagInput + '-preview',
                    name: tagInput,
                    color: newTagColor || '#ffffff',
                  }}
                  className={clsx(
                    'tw-py-1',
                    'tw-rounded-full',
                    'tw-text-sm',
                    'tw-border-solid',
                    'tw-border-cfo-grey-600',
                    'tw-max-w-[90px]'
                  )}
                  style={{
                    backgroundColor: newTagColor || '#ffffff',
                    borderWidth: '0.5px',
                  }}
                />
              )}
              {tagInput.trim() &&
                suggestions.filter((s) => s.name.toLowerCase().includes(tagInput.toLowerCase()))
                  .length > 0 && (
                  <div className={clsx('tw-flex', 'tw-flex-wrap', 'tw-gap-y-[6px]', 'tw-mt-[6px]')}>
                    {suggestions
                      .filter((s) => s.name.toLowerCase().includes(tagInput.toLowerCase()))
                      .slice(0, 3)
                      .map((s) => {
                        return (
                          <button
                            key={s.name}
                            onClick={() => onSelectSuggestion(s.name)}
                            className={clsx(
                              'tw-px-3',
                              'tw-py-1',
                              'tw-rounded-full',
                              'tw-border',
                              'tw-text-sm',
                              'tw-font-medium',
                              'tw-shadow-sm',
                              'tw-transition',
                              'hover:tw-scale-105',
                              s.color || 'tw-bg-gray-200',
                              'tw-text-gray-800',
                              'hover:tw-bg-blue-100'
                            )}
                            style={{
                              minWidth: 80,
                              backgroundColor: s.color || '#ffffff',
                              color: isHexColorDark(s.color) === 'dark' ? 'white' : 'black',
                              textAlign: 'left',
                            }}
                          >
                            {s.name}
                          </button>
                        );
                      })}
                  </div>
                )}
            </div>
            <div
              className={clsx('tw-flex', 'tw-items-center', 'tw-gap-2', 'tw-ml-auto')}
              style={{ minHeight: 32 }}
            >
              <button
                type="button"
                onClick={() => setShowColorPicker(!showColorPicker)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label="Pick tag color"
              >
                <Icon
                  icon={ICONS.palette}
                  className="tw-text-[16px]"
                  style={{ position: 'absolute', top: '6px', right: '75px' }}
                />
              </button>
              {showColorPicker && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div
                  ref={colorPickerRef}
                  style={{
                    width: 100,
                    minWidth: 100,
                    maxWidth: 100,
                    zIndex: 3,
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <ColorPicker
                    onSelect={(colorObj) => {
                      setNewTagColor(colorObj.hex);
                    }}
                    color={newTagColor}
                    value={newTagColor}
                    forceShowPicker
                  />
                </div>
              )}
              <button
                onClick={() => {
                  if (tagInput.trim()) {
                    onCreateTag(tagInput.trim(), newTagColor || '#ffffff');
                    showToast('success', 'Tag created successfully');
                    setTagInput('');
                  }
                }}
                disabled={!tagInput.trim()}
                className={clsx(
                  'tw-px-4',
                  'tw-py-2',
                  'tw-rounded-md',
                  'tw-text-white',
                  'tw-text-sm',
                  tagInput.trim()
                    ? 'tw-bg-blue-500 hover:tw-bg-blue-600'
                    : 'tw-bg-gray-300 tw-cursor-not-allowed'
                )}
              >
                Create
              </button>
            </div>
          </div>
          <hr className={clsx('tw-mt-0', 'tw-mb-0', 'tw-border-gray-200')} />
        </>
      )}
    </>
  );
};

export default TagInputCreate;
